import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BrokerClientApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { formatCurrency, formatPhone, snackbarError } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Query from 'devextreme/data/query';

export const BrokerAgreementSelect = ({
    brokerAgreementId,
    onSelect = null,
    disabled = false,
    required = true,
    searchText = null,
    showHeader = false,
}) => {
    const PER_PAGE = 20;
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

    const [cache, set_cache] = useState({});
    const [searchLoading, set_searchLoading] = useState(false);
    const [agreements, set_agreements] = useState();
    const [query, set_query] = useState('');
    const [agreement, set_agreement] = useState();
    const [entityId, set_entityId] = useState();
    const [entityName, set_entityName] = useState();

    useEffect(() => {
        async function brokerAgreementIdChanged() {
            if (!brokerAgreementId) {
                set_agreement(null);
                return;
            }

            const response = await brokerClientApi.getBrokerAgreementSummary(brokerAgreementId);
            if (response.statusCode !== 200) return;

            const bwr = response.result;
            set_agreement(bwr);
            set_entityId(bwr.entityId);
            set_entityName(bwr.borrower.borrowerEntity);
            onSelect && onSelect(bwr);
        }

        brokerAgreementIdChanged();
    }, [brokerAgreementId]);

    useEffect(() => {
        if (!searchText) {
            return;
        }

        handleAgreementsSearch(searchText);
    }, [searchText]);

    const searchAgreements = async (query, page = 1) => {
        return await brokerClientApi.searchBrokerAgreements(query, page, PER_PAGE);
    };

    const handleSearchInputChange = (q) => {
        set_agreement();
        q = (q ?? '').toLowerCase();
        set_query(q);
    };

    const handleAgreementsSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (cache[q]) {
            set_agreements(cache[q].options);
            return;
        }

        set_searchLoading(true);
        searchAgreements(q).then((resp) => {
            cache[q] = { options: resp.result, page: 1 };
            set_cache(cache);
            set_searchLoading(false);
            set_agreements(resp.result);
        });
    }, []);

    const handlePagination = (e, shownResults) => {
        const cachedQuery = cache[query];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);
        const page = cachedQuery.page + 1;
        searchAgreements(query, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            cache[query] = { ...cachedQuery, options, page };
            set_cache(cache);
            set_searchLoading(false);
            set_agreements(options);
        });
    };

    const selectAgreement = (borrower) => {
        set_agreement(borrower);
        onSelect && onSelect(borrower, cancelSelect);
    };

    const cancelSelect = () => {
        set_agreement(null);
    };

    const clearSelected = () => {
        set_agreement(null);
        onSelect && onSelect(null, cancelSelect);
    };

    const filterByFields = [
        'brokerBusinessName',
        'brokerContactName',
        'brokerEmail',
        'borrowerEntity',
        'borrowerName',
        'borrowerEmail',
    ];

    const onEntitySave = async (e) => {
        e.preventDefault();

        if (!agreement) return;

        const response = await brokerClientApi.updateBrokerAgreementEntity({
            brokerAgreementId: agreement.brokerAgreementId,
            entityId: entityId,
        });

        if (response.statusCode === 200) {
            agreement.borrowerEntities.forEach((b) => {
                if (b.entityId.toString() === entityId) {
                    set_entityName(b.entityName);
                }
            });
        } else {
            snackbarError('Could not update the entity.');
        }
    };

    return (
        <>
            <div className='type-ahead-container'>
                {showHeader && <label htmlFor='borrower-search'>B-Client {required && <span className='req'>*</span>}</label>}
                <AsyncTypeahead
                    className='type-ahead'
                    id='agereements-search'
                    isLoading={searchLoading}
                    labelKey={(option) =>
                        `${option?.brokerContactName} - ${option?.brokerBusinessName ?? ''},  ${option.borrowerEntity} - ${
                            option.borrowerName
                        }`
                    }
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleSearchInputChange}
                    onPaginate={handlePagination}
                    onSearch={handleAgreementsSearch}
                    onChange={(event) => selectAgreement(event[0])}
                    options={agreements}
                    paginate
                    placeholder='Search for B-Client by Business Name, Contact, Borrower Entity, Name or Email...'
                    renderMenuItemChildren={(option) => (
                        <div
                            key={option.id}
                            style={{ borderBottom: '1px solid silver' }}
                        >
                            <span>
                                <div>
                                    <b>{option.brokerContactName}</b>
                                    {option.brokerBusinessName && <span> - {option.brokerBusinessName}</span>}
                                </div>
                                {option.tin && <div>TIN: {option.tin}</div>}
                                {option.brokerEmail && <div>{option.brokerEmail}</div>}
                                {(option.borrowerEntity || option.borrowerName) && (
                                    <div>
                                        Entity:{' '}
                                        <b>
                                            {option.borrowerEntity}{' '}
                                            {option.borrowerName && <span> ({option?.borrowerName})</span>}
                                            {(option.borrowerCity || option.borrowerState) && (
                                                <span>
                                                    - {option?.borrowerCity}, {option?.borrowerState}
                                                </span>
                                            )}
                                            {option.borrowerEmail && (
                                                <span>
                                                    <br /> {option.borrowerEmail}
                                                </span>
                                            )}
                                        </b>
                                    </div>
                                )}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={agreement ? [agreement] : []}
                    disabled={disabled || agreement}
                    inputProps={{ required: required }}
                    filterBy={filterByFields}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => clearSelected()}
                    disabled={disabled || !agreement}
                    title='Remove selected Broker Agreement'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            {agreement && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        display: 'inline-flex',
                        width: '800px',
                    }}
                >
                    {agreement.broker && (
                        <div style={{ width: '40%' }}>
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    color: 'rgb(44 85 145)',
                                }}
                            >
                                Broker:
                            </div>
                            <div
                                style={{
                                    paddingRight: '20px',
                                    fontSize: '13px',
                                }}
                            >
                                {agreement.broker.fullName && (
                                    <div>
                                        <b>{agreement.broker.fullName}</b>
                                    </div>
                                )}
                                {agreement.broker.businessName && (
                                    <div>
                                        <b>{agreement.broker.businessName}</b>
                                    </div>
                                )}
                                {agreement.broker.street1 && <div>{agreement.broker.street1}</div>}
                                {agreement.broker.street2 && <div>{agreement.broker.street2}</div>}
                                {agreement.broker.city && agreement.broker.state && agreement.broker.zipCode && (
                                    <div>
                                        {agreement.broker.city}, {agreement.broker.state} {agreement.broker.zipCode}
                                    </div>
                                )}
                                {agreement.broker.email && <div>{agreement.broker.email}</div>}
                                {agreement.broker.phone && <div>{formatPhone(agreement.broker.phone)}</div>}
                            </div>
                        </div>
                    )}
                    {agreement.borrower && (
                        <div style={{ width: '60%' }}>
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    color: 'rgb(44 85 145)',
                                }}
                            >
                                Borrower:
                            </div>
                            <div style={{ clear: 'both' }}>
                                <div
                                    style={{
                                        width: '65%',
                                        float: 'left',
                                        paddingRight: '20px',
                                        fontSize: '13px',
                                    }}
                                >
                                    {entityName && (
                                        <div>
                                            <b>{entityName}</b>
                                        </div>
                                    )}
                                    {agreement.borrower.name && (
                                        <div>
                                            <b>{agreement.borrower.name}</b>
                                        </div>
                                    )}
                                    {agreement.borrower.tin && <div>TIN: {agreement.borrower.tin}</div>}
                                    {agreement.borrower.businessAddress?.street1 && (
                                        <div>{agreement.borrower.businessAddress?.street1}</div>
                                    )}
                                    {agreement.borrower.businessAddress?.street2 && (
                                        <div>{agreement.borrower.businessAddress?.street2}</div>
                                    )}
                                    {agreement.borrower.businessAddress?.city &&
                                        agreement.borrower.businessAddress?.state &&
                                        agreement.borrower.businessAddress?.zipCode && (
                                            <div>
                                                {agreement.borrower.businessAddress?.city},{' '}
                                                {agreement.borrower.businessAddress?.state}{' '}
                                                {agreement.borrower.businessAddress?.zipCode}
                                            </div>
                                        )}
                                    {agreement.borrower.email && <div>{agreement.borrower.email}</div>}
                                    {agreement.borrower.phone && <div>{formatPhone(agreement.borrower.phone)}</div>}
                                    {agreement.borrower.userName && <div>User: {agreement.borrower.userName}</div>}
                                </div>
                                <div
                                    style={{
                                        width: '35%',
                                        float: 'left',
                                        fontSize: '13px',
                                    }}
                                >
                                    <div>
                                        <div>
                                            <b>Net Worth</b>
                                        </div>
                                        <div>
                                            Regular: &nbsp;&nbsp;
                                            {agreement.borrower?.netWorth
                                                ? formatCurrency(agreement.borrower.netWorth, 0)
                                                : 'Not set'}
                                        </div>
                                        <div>
                                            Adjusted:&nbsp;&nbsp;
                                            {agreement.borrower?.adjustedNetWorth
                                                ? formatCurrency(agreement.borrower.adjustedNetWorth, 0)
                                                : 'Not set'}
                                        </div>
                                    </div>
                                    {/* {agreement.borrower?.creditScores && agreement.borrower.creditScores.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <DataGrid
                        dataSource={agreement.borrower?.creditScores}
                        showBorders={true}
                        showRowLines={false}
                        showColumnHeaders={false}
                        rowAlternationEnabled={false}
                        columnMinWidth={50}
                        columnAutoWidth={false}
                      >
                        <Column dataField="name" caption="" dataType="string" width={200} />
                        <Column dataField="score1" caption="" dataType="string" width={50} />
                        <Column dataField="score2" caption="" dataType="string" width={50} />
                      </DataGrid>
                    </div>
                  )} */}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            Update Primary Entity:
            <select
                id='primaryEntity'
                value={entityId}
                onChange={(event) => set_entityId(event.target.value)}
                style={{ width: 200, margin: 10 }}
            >
                {agreement &&
                    agreement.borrowerEntities?.map((b) => {
                        return (
                            <option
                                key={b.entityId}
                                value={b.entityId}
                            >
                                {b.entityName}
                            </option>
                        );
                    })}
            </select>
            <button
                className='button primary'
                style={{ margin: 'auto', padding: 5 }}
                onClick={onEntitySave}
            >
                Update Entity
            </button>
        </>
    );
};
